import { useCallback, useState } from 'react';
import { Lease, LeaseInput } from '../../types/leases';
import { FileAttachment } from '../../types/attachments';
import { leasesApi } from '../../api/leasesApi';
import { uploadLeaseContract } from '../../Helpers/leaseContractUtils';
import { requireUser } from '../../api/adminGuard';

export interface LeaseMutations {
    submitting: boolean;
    error: string | null;
    clearError: () => void;
    createLease: (input: LeaseInput) => Promise<Lease>;
    /** Creates the lease, then uploads and attaches each contract file; the lease exists even if an upload fails. */
    createLeaseWithContracts: (input: LeaseInput, filesToUpload: File[]) => Promise<Lease>;
    updateLease: (leaseKey: string, input: LeaseInput) => Promise<void>;
    endLease: (leaseKey: string, endDate?: string) => Promise<void>;
    deleteLease: (leaseKey: string) => Promise<void>;
    addContract: (leaseKey: string, attachment: FileAttachment) => Promise<void>;
    removeContract: (leaseKey: string, attachmentKey: string) => Promise<void>;
    renameContract: (leaseKey: string, attachmentKey: string, displayName: string | undefined) => Promise<void>;
}

/**
 * Lease mutations shared by Hyresavtal (/leases), the tenant page and the
 * parking area page. `onMutated` runs after every successful write so the
 * caller can reload what it shows (its leases, and the building when the
 * spot's currentLeaseKey changed — plan D11, review F4).
 */
export const useLeaseMutations = (onMutated: () => Promise<void>): LeaseMutations => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = useCallback(async <T,>(fallback: string, action: () => Promise<T>): Promise<T> => {
        setSubmitting(true);
        setError(null);
        try {
            const result = await action();
            await onMutated();
            return result;
        } catch (err) {
            const message = err instanceof Error ? err.message : fallback;
            console.error(fallback, err);
            setError(message);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [onMutated]);

    const createLease = useCallback((input: LeaseInput) => run('Kunde inte skapa hyresavtal', () => leasesApi.create(input)), [run]);
    const createLeaseWithContracts = useCallback((input: LeaseInput, filesToUpload: File[]) => run('Kunde inte skapa hyresavtal', async () => {
        const lease = await leasesApi.create(input);
        if (filesToUpload.length === 0) return lease;
        const uid = requireUser('ladda upp avtal');
        for (const file of filesToUpload) {
            const result = await uploadLeaseContract(lease.key, file, uid);
            if (result.success && result.attachment) {
                await leasesApi.addContract(lease.key, result.attachment);
            } else if (result.error) {
                throw new Error(result.error);
            }
        }
        return lease;
    }), [run]);
    const updateLease = useCallback((leaseKey: string, input: LeaseInput) => run('Kunde inte uppdatera hyresavtal', () => leasesApi.update(leaseKey, input)), [run]);
    const endLease = useCallback((leaseKey: string, endDate?: string) => run('Kunde inte avsluta hyresavtal', () => leasesApi.end(leaseKey, endDate)), [run]);
    const deleteLease = useCallback((leaseKey: string) => run('Kunde inte ta bort hyresavtal', () => leasesApi.delete(leaseKey)), [run]);
    const addContract = useCallback((leaseKey: string, attachment: FileAttachment) => run('Kunde inte lägga till avtal', () => leasesApi.addContract(leaseKey, attachment)), [run]);
    const removeContract = useCallback((leaseKey: string, attachmentKey: string) => run('Kunde inte ta bort avtal', () => leasesApi.removeContract(leaseKey, attachmentKey)), [run]);
    const renameContract = useCallback((leaseKey: string, attachmentKey: string, displayName: string | undefined) => run('Kunde inte byta namn på avtal', () => leasesApi.renameContract(leaseKey, attachmentKey, displayName)), [run]);

    return {
        submitting,
        error,
        clearError: useCallback(() => setError(null), []),
        createLease,
        createLeaseWithContracts,
        updateLease,
        endLease,
        deleteLease,
        addContract,
        removeContract,
        renameContract,
    };
};
